<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceStore } from '@/views/Devices/store'
import { useDeviceLoader } from '@/composables/useDeviceLoader'

// Store
const measurementsStore = useMeasurementsStore()
const deviceStore = useDeviceStore()
const deviceLoader = useDeviceLoader()

// Helper para obtener nombre del device por id
const getDeviceName = (deviceId: string): string => {
    const device = deviceStore.devices.find(d => d.id === deviceId)
    return device?.name || deviceId
}

const copiedId = ref('')
const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    copiedId.value = text
    setTimeout(() => { copiedId.value = '' }, 2000)
}

// Local state
const selectedMeasurement = ref<DeviceSample | null>(null)
const showEditDialog = ref(false)
const showViewDialog = ref(false)
const filterStatus = ref('all') // all, completed, pending
const searchQuery = ref('')
const itemsPerPage = ref(10)
const currentPage = ref(1)

// Form data for editing
const editForm = ref({
  growthPercent: 0,
  newRootsCount: 0,
  rootHealthScore: 5,
  rootColor: '',
  growthPattern: '',
  dominantDirection: '',
  fungalPresence: false,
  imageQuality: '',
  observation: ''
})

// Options for form selects
const rootColorOptions = [
  'Blanco saludable',
  'Blanco cremoso',
  'Amarillento claro',
  'Amarillento oscuro',
  'Marrón claro',
  'Marrón oscuro',
  'Gris',
  'Negro (deteriorado)'
]

const growthPatternOptions = [
  'Lineal',
  'Ramificada',
  'Ramificada densa',
  'Irregular',
  'Radial',
  'Espiral'
]

const directionOptions = [
  'Norte',
  'Norte-Este',
  'Este',
  'Sur-Este',
  'Sur',
  'Sur-Oeste',
  'Oeste',
  'Norte-Oeste',
  'Multidireccional',
  'Sin dirección clara'
]

const imageQualityOptions = [
  'Excelente',
  'Buena',
  'Media',
  'Baja',
  'Borrosa',
  'Inutilizable'
]

// Computed properties
const filteredMeasurements = computed(() => {
  let filtered = measurementsStore.deviceSamples

  // Filter by status
  if (filterStatus.value === 'completed') {
    filtered = filtered.filter(m => m.managed)
  } else if (filterStatus.value === 'pending') {
    filtered = filtered.filter(m => !m.managed)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(m =>
      m.id.toLowerCase().includes(query) ||
      m.deviceMeasurementsId.toLowerCase().includes(query) ||
      m.observation.toLowerCase().includes(query) ||
      m.rootColor.toLowerCase().includes(query)
    )
  }

  return filtered
})

const paginatedMeasurements = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredMeasurements.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredMeasurements.value.length / itemsPerPage.value)
})

// Methods
const openEditDialog = (deviceSample: DeviceSample) => {
  selectedMeasurement.value = deviceSample
  editForm.value = {
    growthPercent: deviceSample.growthPercent,
    newRootsCount: deviceSample.newRootsCount,
    rootHealthScore: deviceSample.rootHealthScore,
    rootColor: deviceSample.rootColor,
    growthPattern: deviceSample.growthPattern,
    dominantDirection: deviceSample.dominantDirection,
    fungalPresence: deviceSample.fungalPresence,
    imageQuality: deviceSample.imageQuality,
    observation: deviceSample.observation
  }
  showEditDialog.value = true
}

const openViewDialog = (deviceSample: DeviceSample) => {
  selectedMeasurement.value = deviceSample
  showViewDialog.value = true
}

const closeEditDialog = () => {
  showEditDialog.value = false
  resetForm()
  selectedMeasurement.value = null
}

const closeViewDialog = () => {
  showViewDialog.value = false
  selectedMeasurement.value = null
}

const resetForm = () => {
  editForm.value = {
    growthPercent: 0,
    newRootsCount: 0,
    rootHealthScore: 5,
    rootColor: '',
    growthPattern: '',
    dominantDirection: '',
    fungalPresence: false,
    imageQuality: '',
    observation: ''
  }
}

const saveMeasurement = async () => {
  console.log('Saving measurement with form data:', editForm.value)
  if (!selectedMeasurement.value) {
    console.error('No measurement selected')
    return
  }

  try {
    const updatedMeasurement = new DeviceSample({
      ...selectedMeasurement.value,
      ...editForm.value,
      managed: true,
      managedAt: new Date(),
      updatedBy: 'admin' // TODO: Get from auth store
    })
    console.log('Saving deviceSample:', updatedMeasurement)
    await measurementsStore.putMeasurement(updatedMeasurement)

    // Cerrar ambos diálogos después de guardar exitosamente
    closeEditDialog()
    closeViewDialog()

    // Mostrar notificación de éxito (opcional)
    console.log('Medición guardada exitosamente')
  } catch (error) {
    console.error('Error al guardar medición:', error)
  }
}

const getStatusColor = (deviceSample: DeviceSample) => {
  if (deviceSample.managed) return 'success'

  const daysPending = deviceSample.getPendingDays()
  if (daysPending > 3) return 'error'
  if (daysPending > 1) return 'warning'
  return 'info'
}

const getStatusText = (deviceSample: DeviceSample) => {
  if (deviceSample.managed) return 'Completada'

  const daysPending = deviceSample.getPendingDays()
  if (daysPending > 3) return `Crítica (${daysPending}d)`
  if (daysPending > 1) return `Pendiente (${daysPending}d)`
  return 'Nueva'
}

const getPriorityIcon = (deviceSample: DeviceSample) => {
  if (deviceSample.managed) return 'mdi-check-circle'

  const daysPending = deviceSample.getPendingDays()
  if (daysPending > 3) return 'mdi-alert-octagon'
  if (daysPending > 1) return 'mdi-clock-alert'
  return 'mdi-clock-outline'
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    measurementsStore.getMeasurements(),
    deviceLoader.loadDevicesByRole()
  ])
})
</script>

<template>
  <v-container fluid>
    <!-- Header Section -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold">Gestión de Mediciones</h1>
            <p class="text-body-1 text-medium-emphasis">Análisis manual de imágenes de crecimiento radicular</p>
          </div>
          <div>
            <v-btn color="primary" prepend-icon="mdi-refresh" @click="measurementsStore.getMeasurements">
              Actualizar
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#FFF3E0" size="48" class="mr-3">
                <v-icon color="#FF9800" size="24">mdi-clipboard-list</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Total Mediciones</div>
                <div class="text-h4 font-weight-bold">{{ measurementsStore.totalMeasurements }}</div>
                <div class="text-caption text-success">Sistema activo</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear model-value="100" height="6" color="#FF9800" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#FFEBEE" size="48" class="mr-3">
                <v-icon color="#F44336" size="24">mdi-clock-alert</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Pendientes</div>
                <div class="text-h4 font-weight-bold">{{ measurementsStore.uncompletedCount }}</div>
                <div class="text-caption"
                  :class="measurementsStore.uncompletedCount > 5 ? 'text-error' : 'text-warning'">
                  {{ measurementsStore.pendingOldest > 0 ? `Más antigua: ${measurementsStore.pendingOldest}d` : 'Al día'
                  }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear
              :model-value="(measurementsStore.uncompletedCount / measurementsStore.totalMeasurements) * 100" height="6"
              color="#F44336" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#E8F5E8" size="48" class="mr-3">
                <v-icon color="#4CAF50" size="24">mdi-check-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Tasa de Finalización</div>
                <div class="text-h4 font-weight-bold">{{ measurementsStore.completionRate }}%</div>
                <div class="text-caption text-success">{{ measurementsStore.completedCount }} completadas</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear :model-value="measurementsStore.completionRate" height="6" color="#4CAF50"
              rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text class="pb-0">
            <div class="d-flex align-center">
              <v-avatar color="#E3F2FD" size="48" class="mr-3">
                <v-icon color="#2196F3" size="24">mdi-timer-outline</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-medium-emphasis">Tiempo Promedio</div>
                <div class="text-h4 font-weight-bold">{{ measurementsStore.averageResponseTime }}h</div>
                <div class="text-caption text-info">Por medición</div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-progress-linear :model-value="Math.min((24 - measurementsStore.averageResponseTime) / 24 * 100, 100)"
              height="6" color="#2196F3" rounded></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters and Search -->
    <v-row>
      <v-col cols="12">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="searchQuery" label="Buscar mediciones" prepend-inner-icon="mdi-magnify"
                  variant="outlined" density="comfortable" hide-details clearable></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-select v-model="filterStatus" :items="[
                  { title: 'Todas las mediciones', value: 'all' },
                  { title: 'Completadas', value: 'completed' },
                  { title: 'Pendientes', value: 'pending' }
                ]" label="Estado" variant="outlined" density="comfortable" hide-details></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-select v-model="itemsPerPage" :items="[5, 10, 25, 50]" label="Elementos por página"
                  variant="outlined" density="comfortable" hide-details></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- DeviceSamples Table -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card flat elevation="2" class="rounded-lg">
          <v-card-title class="d-flex justify-space-between align-center px-6 py-4">
            <div class="text-h6 font-weight-bold">
              Mediciones
              <v-chip size="small" color="primary" class="ml-2">
                {{ filteredMeasurements.length }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="px-0">
            <v-table>
              <thead>
                <tr>
                  <th class="text-left px-6">ID</th>
                  <th class="text-left">Dispositivo</th>
                  <th class="text-left">Estado</th>
                  <th class="text-left">Crecimiento</th>
                  <th class="text-left">Salud</th>
                  <th class="text-left">Raíces Nuevas</th>
                  <th class="text-left">Tiene foto</th>
                  <th class="text-left">Creada</th>
                  <th class="text-left">Actualizada</th>
                  <th class="text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="deviceSample in paginatedMeasurements" :key="deviceSample.id" class="deviceSample-row"
                  :class="{ 'priority-high': !deviceSample.managed && deviceSample.getPendingDays() > 3 }">
                  <td class="px-6">
                    <div class="d-flex align-center">
                      <v-icon :icon="getPriorityIcon(deviceSample)" :color="getStatusColor(deviceSample)" size="16"
                        class="mr-2"></v-icon>
                      <span class="font-family-monospace text-body-2">{{ deviceSample.id.slice(0, 8) }}...</span>
                      <v-btn icon size="x-small" variant="text" @click="copyToClipboard(deviceSample.id)">
                        <v-icon size="14">mdi-content-copy</v-icon>
                        <v-tooltip activator="parent" location="top">Copiar ID completo</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-medium">{{ getDeviceName(deviceSample.deviceId) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ deviceSample.deviceId }}</div>
                  </td>
                  <td>
                    <v-chip :color="getStatusColor(deviceSample)" size="small" text-color="white">
                      {{ getStatusText(deviceSample) }}
                    </v-chip>
                  </td>
                  <td>
                    <div v-if="deviceSample.managed" class="d-flex align-center">
                      <span class="font-weight-medium">{{ deviceSample.growthPercent }}%</span>
                      <v-chip size="x-small"
                        :color="deviceSample.growthPercent >= 3 ? 'success' : deviceSample.growthPercent >= 1 ? 'warning' : 'default'"
                        class="ml-2">
                        {{ deviceSample.getGrowthCategory() }}
                      </v-chip>
                    </div>
                    <span v-else class="text-medium-emphasis">-</span>
                  </td>
                  <td>
                    <div v-if="deviceSample.managed" class="d-flex align-center">
                      <v-icon :color="deviceSample.getHealthScoreColor()" size="16" class="mr-1">
                        mdi-heart
                      </v-icon>
                      <span>{{ deviceSample.rootHealthScore }}/10</span>
                    </div>
                    <span v-else class="text-medium-emphasis">-</span>
                  </td>
                  <td>
                    <div v-if="deviceSample.managed" class="d-flex align-center">
                      <v-icon color="success" size="16" class="mr-1">mdi-sprout</v-icon>
                      <span>{{ deviceSample.newRootsCount }}</span>
                    </div>
                    <span v-else class="text-medium-emphasis">-</span>
                  </td>
                          <td>
                    <div class="text-body-2">{{ deviceSample.imageUrl? 'Sí' : 'No' }}</div>
                    <div class="text-caption text-medium-emphasis">{{ deviceSample.createdBy }}</div>
                  </td>
                  <td>
                    <div class="text-body-2">{{ formatDate(deviceSample.time) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ deviceSample.createdBy }}</div>
                  </td>
                  <td>
                    <div class="text-body-2">{{ formatDate(deviceSample.updatedAt) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ deviceSample.updatedBy || '-' }}</div>
                  </td>

                  <td>
                    <div class="d-flex gap-1">
                      <v-btn icon size="small" color="primary" variant="text" @click="openEditDialog(deviceSample)">
                        <v-icon size="18">mdi-pencil</v-icon>
                        <v-tooltip activator="parent" location="top">Completar análisis</v-tooltip>
                      </v-btn>

                      <v-btn icon size="small" color="info" variant="text" @click="openViewDialog(deviceSample)">
                        <v-icon size="18">mdi-eye</v-icon>
                        <v-tooltip activator="parent" location="top">Ver detalles</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- Pagination -->
            <div class="d-flex justify-center align-center pa-4" v-if="totalPages > 1">
              <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7"
                color="primary"></v-pagination>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="showEditDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="text-h5 px-6 py-4 bg-primary text-white">
          <v-icon class="mr-2">mdi-clipboard-edit</v-icon>
          Completar Análisis de Medición
        </v-card-title>

        <v-card-text class="pa-6">
          <div v-if="selectedMeasurement" class="mb-4">
            <!-- Imagen de la muestra -->
            <div v-if="selectedMeasurement.imageUrl" class="position-relative mb-4">
              <v-img
                :src="selectedMeasurement.imageUrl"
                max-height="300"
                class="rounded-lg"
                cover
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-btn
                icon
                size="small"
                color="white"
                class="position-absolute"
                style="top: 8px; right: 8px;"
                :href="selectedMeasurement.imageUrl"
                target="_blank"
              >
                <v-icon size="18">mdi-open-in-new</v-icon>
                <v-tooltip activator="parent" location="top">Abrir imagen en nueva pestaña</v-tooltip>
              </v-btn>
            </div>
            <v-alert type="info" variant="tonal" class="mb-4">
              <div class="text-body-2">
                <strong>ID:</strong> {{ selectedMeasurement.id }}<br>
                <strong>Dispositivo:</strong> {{ selectedMeasurement.deviceMeasurementsId }}<br>
                <strong>Creada:</strong> {{ formatDate(selectedMeasurement.createdAt) }}
              </div>
            </v-alert>
          </div>

          <v-form>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="editForm.growthPercent" label="Porcentaje de Crecimiento (%)"
                  type="number" step="0.1" min="0" max="100" variant="outlined" density="comfortable" suffix="%"
                  :rules="[v => v >= 0 || 'Debe ser mayor o igual a 0']"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model.number="editForm.newRootsCount" label="Cantidad de Raíces Nuevas" type="number"
                  min="0" variant="outlined" density="comfortable"
                  :rules="[v => v >= 0 || 'Debe ser mayor o igual a 0']"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-slider v-model="editForm.rootHealthScore" label="Puntaje de Salud Radicular" min="1" max="10"
                  step="1" thumb-label show-ticks tick-size="4"
                  :color="editForm.rootHealthScore >= 7 ? 'success' : editForm.rootHealthScore >= 4 ? 'warning' : 'error'"></v-slider>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="editForm.imageQuality" :items="imageQualityOptions" label="Calidad de Imagen"
                  variant="outlined" density="comfortable" :rules="[v => !!v || 'Campo requerido']"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="editForm.rootColor" :items="rootColorOptions" label="Color de Raíces"
                  variant="outlined" density="comfortable" :rules="[v => !!v || 'Campo requerido']"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="editForm.growthPattern" :items="growthPatternOptions" label="Patrón de Crecimiento"
                  variant="outlined" density="comfortable" :rules="[v => !!v || 'Campo requerido']"></v-select>
              </v-col>

              <v-col cols="12">
                <v-select v-model="editForm.dominantDirection" :items="directionOptions" label="Dirección Dominante"
                  variant="outlined" density="comfortable"></v-select>
              </v-col>

              <v-col cols="12">
                <v-checkbox v-model="editForm.fungalPresence" label="Presencia de Hongos o Daños"
                  color="warning"></v-checkbox>
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="editForm.observation" label="Observaciones Generales" variant="outlined" rows="4"
                  placeholder="Describe cualquier observación adicional sobre el crecimiento radicular..."></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeEditDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="saveMeasurement" :loading="measurementsStore.loading">
            Guardar Análisis
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Dialog -->
    <v-dialog v-model="showViewDialog" max-width="700">
      <v-card v-if="selectedMeasurement">
        <v-card-title class="text-h5 px-6 py-4 bg-info text-white">
          <v-icon class="mr-2">mdi-eye</v-icon>
          Detalles de Medición
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <!-- Imagen de la muestra -->
              <div v-if="selectedMeasurement.imageUrl" class="position-relative mb-4">
                <v-img
                  :src="selectedMeasurement.imageUrl"
                  max-height="350"
                  class="rounded-lg"
                  cover
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-btn
                  icon
                  size="small"
                  color="white"
                  class="position-absolute"
                  style="top: 8px; right: 8px;"
                  :href="selectedMeasurement.imageUrl"
                  target="_blank"
                >
                  <v-icon size="18">mdi-open-in-new</v-icon>
                  <v-tooltip activator="parent" location="top">Abrir imagen en nueva pestaña</v-tooltip>
                </v-btn>
              </div>
              <div class="d-flex justify-space-between align-center mb-4">
                <div>
                  <div class="text-h6 font-weight-bold">{{ selectedMeasurement.id }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ selectedMeasurement.deviceMeasurementsId }}</div>
                </div>
                <v-chip :color="getStatusColor(selectedMeasurement)" text-color="white">
                  {{ getStatusText(selectedMeasurement) }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" v-if="selectedMeasurement.managed">
              <v-row>
                <v-col cols="6" md="3">
                  <v-card variant="outlined" class="pa-3 text-center">
                    <div class="text-h5 font-weight-bold text-primary">{{ selectedMeasurement.growthPercent }}%</div>
                    <div class="text-caption">Crecimiento</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card variant="outlined" class="pa-3 text-center">
                    <div class="text-h5 font-weight-bold text-success">{{ selectedMeasurement.newRootsCount }}</div>
                    <div class="text-caption">Raíces Nuevas</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card variant="outlined" class="pa-3 text-center">
                    <div class="text-h5 font-weight-bold" :style="{ color: selectedMeasurement.getHealthScoreColor() }">
                      {{ selectedMeasurement.rootHealthScore }}/10
                    </div>
                    <div class="text-caption">Salud</div>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card variant="outlined" class="pa-3 text-center">
                    <div class="text-h6 font-weight-bold">{{ selectedMeasurement.getGrowthCategory() }}</div>
                    <div class="text-caption">Categoría</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <v-list>
                <v-list-item>
                  <v-list-item-title>Color de Raíces</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedMeasurement.rootColor || 'No especificado' }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Patrón de Crecimiento</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedMeasurement.growthPattern || 'No especificado'
                  }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Dirección Dominante</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedMeasurement.dominantDirection || 'No especificado'
                  }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Calidad de Imagen</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedMeasurement.imageQuality || 'No especificado'
                  }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Presencia de Hongos</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip size="small" :color="selectedMeasurement.fungalPresence ? 'error' : 'success'"
                      text-color="white">
                      {{ selectedMeasurement.fungalPresence ? 'Sí' : 'No' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-if="selectedMeasurement.observation">
                  <v-list-item-title>Observaciones</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedMeasurement.observation }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" v-else>
              <v-alert type="warning" variant="tonal">
                Esta medición aún no ha sido completada. Requiere análisis manual.
              </v-alert>
            </v-col>

            <v-col cols="12">
              <v-divider class="mb-4"></v-divider>
              <div class="text-body-2 text-medium-emphasis">
                <div><strong>Creada:</strong> {{ formatDate(selectedMeasurement.createdAt) }} por {{
                  selectedMeasurement.createdBy }}</div>
                <div v-if="selectedMeasurement.managed">
                  <strong>Completada:</strong> {{ formatDate(selectedMeasurement.managedAt) }} por {{
                    selectedMeasurement.updatedBy }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn v-if="!selectedMeasurement.managed" color="primary" prepend-icon="mdi-pencil"
            @click="openEditDialog(selectedMeasurement)">
            Completar Análisis
          </v-btn>
          <v-btn variant="text" @click="closeViewDialog">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.deviceSample-row {
  transition: background-color 0.2s ease;
}

.deviceSample-row:hover {
  background-color: #f5f5f5;
}

.deviceSample-row.priority-high {
  border-left: 4px solid #f44336;
  background-color: #ffebee;
}

.deviceSample-row.priority-high:hover {
  background-color: #ffcdd2;
}

.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}
</style>
